import { useCallback, useEffect, useRef, useState } from 'react';
import { autoCorrelate } from '@/lib/pitch';
import { transportBus } from '@/lib/transportBus';

export function usePitchDetector({ echoCancellation = false } = {}) {
  const echoRef = useRef(echoCancellation);
  const [status, setStatus] = useState('idle'); // idle | listening | denied | error
  const [freq, setFreq] = useState(null);
  const ref = useRef({});
  const resumeOnShow = useRef(false);

  const stop = useCallback(() => {
    const r = ref.current;
    if (r.raf) cancelAnimationFrame(r.raf);
    r.session?.offClick?.();
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close().catch(() => {});
    ref.current = {};
    setFreq(null);
    setStatus((s) => (s === 'listening' || s === 'suspended' ? 'idle' : s));
  }, []);

  const start = useCallback(async () => {
    if (ref.current.session) return;
    const session = {};
    ref.current = { session };
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // Echo cancellation removes our own drone from the mic when it is playing.
        audio: { echoCancellation: echoRef.current, noiseSuppression: false, autoGainControl: false },
      });
    } catch (e) {
      if (ref.current.session === session) ref.current = {};
      setStatus(e?.name === 'NotAllowedError' ? 'denied' : 'error');
      return;
    }
    if (ref.current.session !== session) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ref.current = { session, stream, ctx };
    const resume = () => ctx.resume().catch(() => {});
    resume();
    document.addEventListener('pointerdown', resume, { once: true });
    document.addEventListener('keydown', resume, { once: true });
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 4096;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const history = [];
    let lastUpdate = 0;
    // Metronome clicks: skip analysis frames around each one (window covers the 4096-sample buffer).
    const gates = [];
    const winMs = (analyser.fftSize / ctx.sampleRate) * 1000;
    session.offClick = transportBus.onClick((lead) => {
      const at = performance.now() + lead * 1000;
      gates.push([at - 10, at + winMs + 60]);
      if (gates.length > 64) gates.shift();
    });
    setStatus('listening');

    const tick = (ts) => {
      if (ref.current.session !== session) return;
      const state = ctx.state === 'running' ? 'listening' : 'suspended';
      setStatus((s) => (s === state ? s : state));
      const now = performance.now();
      while (gates.length && gates[0][1] < now) gates.shift();
      if (gates.some(([a, b]) => now >= a && now <= b)) {
        ref.current.raf = requestAnimationFrame(tick);
        return;
      }
      analyser.getFloatTimeDomainData(buf);
      const f = autoCorrelate(buf, ctx.sampleRate);
      if (f > 25 && f < 4200) {
        history.push(f);
        if (history.length > 5) history.shift();
      } else if (history.length) {
        history.shift();
      }
      if (ts - lastUpdate > 60) {
        lastUpdate = ts;
        const sorted = [...history].sort((a, b) => a - b);
        setFreq(sorted.length ? sorted[Math.floor(sorted.length / 2)] : null);
      }
      ref.current.raf = requestAnimationFrame(tick);
    };
    ref.current.raf = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        resumeOnShow.current = !!ref.current.session;
        stop();
      } else if (resumeOnShow.current) {
        resumeOnShow.current = false;
        start();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', stop);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', stop);
      stop();
    };
  }, [stop, start]);

  // Restart the mic with the new echo-cancellation setting if it is listening.
  useEffect(() => {
    if (echoRef.current === echoCancellation) return;
    echoRef.current = echoCancellation;
    if (ref.current.session) { stop(); start(); }
  }, [echoCancellation, start, stop]);

  return { status, freq, start, stop };
}
